import React, {useEffect, useState} from 'react';
import {StyleSheet, RefreshControl} from 'react-native';
import {Box, Heading, VStack, HStack, ScrollView} from 'native-base';
import {NativeStackScreenProps} from '@react-navigation/native-stack';
import {useSalesmanContext} from '../../../../../../context/salesman';
import useToast from '../../../../../../hooks';
import {IClient} from '../../../../../../interfaces/dtos';
import {
  BalanceIcon,
  HistoryIcon,
  PaymentsIcon,
  CompanyIcon,
} from './../../../../../../components/icons';
import DashboardTemplate from '../../../../../../components/templates/dashboard';
import {
  BalanceItem,
  DashboardItem,
} from '../../../../../../components/dashboard';
import {StackParamList} from '../../../../../../navigators';
import {SpinnerModal} from '../../../../../../components/modals';
import {message as t} from '../../../../../../utils/messages';
import BillingIcon from '../../../../../../components/icons/billing/BillingIcon';
import SalesOrderIcon from '../../../../../../components/icons/sales-order';

type ScreenProps = NativeStackScreenProps<
  StackParamList,
  'SalesmanClientDashboard'
>;

const clientNameMaxLength = 24;

export const DashboardScreen = ({navigation}: ScreenProps) => {
  const {
    fetchBalanceCustomer,
    balanceCustomer,
    getProfile,
    createSalesmanNewOrder,
    client,
  } = useSalesmanContext();
  const [isLoading, setIsLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const {showServerError} = useToast();

  const profile = getProfile();

  const getBalanceCustomer = async () => {
    if (client && (await fetchBalanceCustomer(client))) {
      return;
    }
    throw new Error('Internal error');
  };

  const getDataClient = async () => {
    try {
      setIsLoading(true);
      await getBalanceCustomer();
      setIsLoading(false);
    } catch (ex) {
      setIsLoading(false);
      showServerError();
    }
  };

  const refreshData = async () => {
    await getDataClient();
    setRefreshing(false);
  };

  useEffect(() => {
    if (client) {
      getDataClient();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client]);

  useEffect(() => {
    if (refreshing) {
      refreshData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshing]);

  const handleShowBalanceList = () => {
    navigation.navigate('BalanceList');
  };

  const handleShowPayments = () => {
    if (client) {
      navigation.navigate('ProofPayment', client as IClient);
    }
  };

  const getCompanyName = () => {
    if (profile && profile.company) {
      return profile.company;
    }
    return '';
  };

  const getUserName = () => {
    if (profile && profile.firstName) {
      return `${profile?.firstName}`;
    }
    return '';
  };

  const handleShowPaymentHistory = async () => {
    navigation.navigate('HistoryList');
  };

  const handleAddNewOrder = async () => {
    if (client) {
      createSalesmanNewOrder(client);
      //navigation.navigate('SalesmanOrderBegin');
      navigation.navigate('OrderDashboardScreen');
    }
  };

  const handleOnRefresh = async () => {
    setRefreshing(true);
  };

  const getClientName = () => {
    const name = client ? (client as IClient).name : '';
    return `${name.substring(0, clientNameMaxLength)}${
      name.length > clientNameMaxLength ? '...' : ''
    }`;
  };

  const handleGoBack = () => {
    navigation.goBack();
  };

  const handleGoToInvoices = () => {
    if (client) {
      createSalesmanNewOrder(client);
      navigation.navigate('InvoiceCandidateList');
    }
  };

  return (
    <DashboardTemplate
      title={getUserName()}
      summary={getCompanyName()}
      backButton={{
        onPress: () => handleGoBack(),
        label: 'go-back',
      }}>
      <ScrollView
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={handleOnRefresh}
            tintColor="transparent"
          />
        }>
        <SpinnerModal
          show={isLoading}
          title={balanceCustomer && balanceCustomer ? t('updating') : undefined}
        />
        <Box borderRadius="md" marginTop={22} marginBottom={10}>
          <VStack space={3} justifyContent="center" marginX={4}>
            <DashboardItem
              label={getClientName()}
              icon={<CompanyIcon width={40} style={styles.balanceIcon} />}
              onPress={() => {}}
            />
            <DashboardItem
              label="dashboard-balances-item"
              icon={<BalanceIcon width={40} style={styles.balanceIcon} />}
              onPress={() => handleShowBalanceList()}
              shadow={true}>
              <BalanceItem
                summary="dashboard-salesman-client-balance-item-label"
                localBalance={balanceCustomer?.local}
                foreignBalance={balanceCustomer?.foreign}
                showAuxLabel={false}
              />
            </DashboardItem>
            <DashboardItem
              title={
                <HStack marginTop={3}>
                  <Heading
                    color={'#06355E'}
                    size="sm"
                    fontWeight="extrabold"
                    fontStyle="italic"
                    textAlign="left"
                    marginTop={3}>
                    {t('dashboard-item-salesman-orders').toUpperCase()}{' '}
                  </Heading>
                </HStack>
              }
              icon={
                <Box marginTop={3}>
                  <SalesOrderIcon width={40} />
                </Box>
              }
              onPress={() => handleAddNewOrder()}
            />
            <DashboardItem
              title={
                <HStack marginTop={3}>
                  <Heading
                    color={'#06355E'}
                    size="sm"
                    fontWeight="extrabold"
                    fontStyle="italic"
                    textAlign="left"
                    marginTop={3}>
                    {t('dashboard-item-salesman-invoices').toUpperCase()}{' '}
                  </Heading>
                </HStack>
              }
              icon={<BillingIcon width={40} />}
              onPress={() => handleGoToInvoices()}
            />
            <DashboardItem
              borderColor={'#FF8C00'}
              title={
                <HStack marginTop={3}>
                  <Heading
                    color={'#FF8C00'}
                    size="sm"
                    fontWeight="extrabold"
                    textAlign="left"
                    marginTop={3}
                    fontStyle="italic">
                    {t('dashboard-item-payment-load').toUpperCase()}{' '}
                  </Heading>
                  <Heading
                    color={'#0088FF'}
                    size="sm"
                    fontWeight="extrabold"
                    textAlign="left"
                    marginTop={3}
                    fontStyle="italic">
                    {t('dashboard-item-payment-payment').toUpperCase()}{' '}
                  </Heading>
                </HStack>
              }
              icon={
                <Box marginTop={3}>
                  <PaymentsIcon width={40} />
                </Box>
              }
              onPress={() => handleShowPayments()}
            />
            <DashboardItem
              title={
                <HStack marginTop={3}>
                  <Heading
                    color={'#06355E'}
                    size="sm"
                    fontWeight="extrabold"
                    fontStyle="italic"
                    textAlign="left"
                    marginTop={3}>
                    {t('dashboard-item-payment-history').toUpperCase()}{' '}
                  </Heading>
                  <Heading
                    color={'#FF8C00'}
                    size="sm"
                    fontWeight="extrabold"
                    textAlign="left"
                    marginTop={3}
                    fontStyle="italic">
                    {t('dashboard-item-payment-load').toUpperCase()}{' '}
                  </Heading>
                  <Heading
                    color={'#0088FF'}
                    size="sm"
                    fontWeight="extrabold"
                    textAlign="left"
                    marginTop={3}
                    fontStyle="italic">
                    {t('dashboard-item-payment-payment').toUpperCase()}{' '}
                  </Heading>
                </HStack>
              }
              icon={<HistoryIcon width={40} />}
              onPress={() => handleShowPaymentHistory()}
            />
          </VStack>
        </Box>
      </ScrollView>
    </DashboardTemplate>
  );
};

const styles = StyleSheet.create({
  balanceIcon: {
    marginTop: 10,
  },
});
